import React, { useState, useEffect } from 'react';
import { bommAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Headset, Loader2, Save, Copy, KeyRound, Send } from 'lucide-react';
import { toast } from 'sonner';

const copy = (value, label) => {
  if (!value) return;
  navigator.clipboard.writeText(value);
  toast.success(`${label} copiado`);
};

export const BommCard = () => {
  const [config, setConfig] = useState(null);
  const [credentials, setCredentials] = useState(null);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [busy, setBusy] = useState(null);

  const load = () => {
    bommAPI.config()
      .then(res => { setConfig(res.data); setWebhookUrl(res.data?.webhook_url || ''); })
      .catch(() => setConfig(null));
  };

  useEffect(() => { load(); }, []);

  const rotate = async () => {
    setBusy('rotate');
    try {
      const res = await bommAPI.rotateCredentials();
      setCredentials(res.data);
      toast.success('Credenciales generadas. Cópialas ahora, no se vuelven a mostrar.');
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error al generar credenciales'); }
    finally { setBusy(null); }
  };

  const saveUrl = async () => {
    setBusy('url');
    try {
      await bommAPI.setWebhookUrl(webhookUrl.trim());
      toast.success('URL de BOMM guardada');
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error al guardar la URL'); }
    finally { setBusy(null); }
  };

  const test = async () => {
    setBusy('test');
    try {
      const res = await bommAPI.test();
      toast.success(res.data?.detail || 'BOMM recibió el evento');
    } catch (err) { toast.error(err?.response?.data?.detail || 'No se pudo contactar a BOMM'); }
    finally { setBusy(null); }
  };

  if (!config) return null;

  return (
    <Card data-testid="settings-bomm-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Headset className="w-5 h-5" /> Soporte con BOMM
          <span className={`ml-1 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${config.configured ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`} data-testid="settings-bomm-status">
            {config.configured ? 'Conectado' : 'Pendiente'}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <p className="text-sm text-muted-foreground">
          Los agentes de soporte gestionan los tickets desde BOMM: leen, responden, cambian el estado,
          se asignan tickets y dejan notas internas que el pastor nunca ve. Las respuestas aparecen en
          el hilo del ticket dentro de la Suite.
        </p>

        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-wide text-muted-foreground">
            Paso 1 · URL base de la API para BOMM
          </Label>
          <div className="flex gap-2">
            <Input readOnly value={config.api_base_url || ''} className="font-mono text-xs" data-testid="settings-bomm-base-url" />
            <Button variant="outline" size="sm" onClick={() => copy(config.api_base_url, 'URL')} data-testid="settings-bomm-copy-url">
              <Copy className="w-4 h-4" />
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Endpoints: GET /tickets · GET /tickets/&#123;id&#125; · POST /tickets/&#123;id&#125;/respond ·
            POST /tickets/&#123;id&#125;/notes · PATCH /tickets/&#123;id&#125; · GET /ping
          </p>
        </div>

        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-wide text-muted-foreground">
            Paso 2 · Credenciales de BOMM
          </Label>
          {credentials ? (
            <div className="space-y-2 p-3 rounded-lg border border-[#056A84]/20 bg-[#056A84]/5" data-testid="settings-bomm-credentials">
              <p className="text-xs text-muted-foreground">
                Cópialas ahora y pégalas en BOMM. No se vuelven a mostrar.
              </p>
              <div className="flex gap-2 items-center">
                <Input readOnly value={credentials.api_key} className="font-mono text-xs" data-testid="settings-bomm-api-key" />
                <Button variant="outline" size="sm" onClick={() => copy(credentials.api_key, 'API key')}>
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex gap-2 items-center">
                <Input readOnly value={credentials.signing_secret} className="font-mono text-xs" data-testid="settings-bomm-secret" />
                <Button variant="outline" size="sm" onClick={() => copy(credentials.signing_secret, 'Signing secret')}>
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Header de identidad: <span className="font-mono">X-BOMM-Key</span> ·
                Firma del cuerpo: <span className="font-mono">X-BOMM-Signature: sha256=HMAC_SHA256(secret, body)</span>
              </p>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">
              {config.api_key_hint
                ? `API key guardada (${config.api_key_hint}) · secreto (${config.signing_secret_hint})`
                : 'Aún no se han generado credenciales para BOMM.'}
            </p>
          )}
          <Button variant="outline" size="sm" onClick={rotate} disabled={busy === 'rotate'} data-testid="settings-bomm-rotate">
            {busy === 'rotate' ? <Loader2 className="w-4 h-4 spinner mr-1" /> : <KeyRound className="w-4 h-4 mr-1" />}
            {config.api_key_hint ? 'Rotar credenciales' : 'Generar credenciales'}
          </Button>
        </div>

        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-wide text-muted-foreground">
            Paso 3 · URL de BOMM para avisarle de tickets nuevos
          </Label>
          <div className="flex gap-2">
            <Input
              value={webhookUrl}
              onChange={e => setWebhookUrl(e.target.value)}
              placeholder="https://bomm.tu-proveedor.com/webhooks/tuiglesia"
              className="font-mono text-xs"
              data-testid="settings-bomm-webhook-input"
            />
            <Button
              className="bg-[#056A84] hover:bg-[#045A70] text-white shrink-0"
              onClick={saveUrl}
              disabled={busy === 'url'}
              data-testid="settings-bomm-webhook-save"
            >
              {busy === 'url' ? <Loader2 className="w-4 h-4 spinner" /> : <Save className="w-4 h-4" />}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Eventos que enviamos: {(config.outbound_events || []).join(', ')} — firmados con el mismo
            signing secret en <span className="font-mono">X-TuIglesia-Signature</span>.
          </p>
        </div>

        <Button variant="outline" size="sm" onClick={test} disabled={busy === 'test'} data-testid="settings-bomm-test">
          {busy === 'test' ? <Loader2 className="w-4 h-4 spinner mr-1" /> : <Send className="w-4 h-4 mr-1" />}
          Enviar evento de prueba a BOMM
        </Button>
      </CardContent>
    </Card>
  );
};

export default BommCard;
